/**
 * Carrier plans: drones that ride a boat and launch from it.
 *
 * The launch itself is the server's job. This client sets a plan up, asks for a
 * launch or a recovery, and reads plans; changes arrive over the socket as
 * `carrier` frames, so nothing here polls.
 */

import { api, type ApiResult } from '@/api/client';
import type { Waypoint } from '@/types/domain';

export type CarrierPhase = 'aboard' | 'launching' | 'airborne' | 'landed';

export interface EmbarkedWire {
  droneId: string;
  /** Index into the boat's uploaded route. */
  deployIndex: number;
  deployLat: number;
  deployLon: number;
  launchAltM: number;
  mission: Waypoint[];
}

export interface CarrierDroneStatus {
  phase: CarrierPhase;
  message: string;
  updatedAt: number;
}

export interface CarrierPlan {
  carrierId: string;
  ownerId: string;
  ownerUsername: string;
  embarked: EmbarkedWire[];
  status: Record<string, CarrierDroneStatus>;
  fired: number[];
  updatedAt: number;
}

export interface EmbarkRequest {
  droneId: string;
  deployIndex: number;
  launchAltM: number;
  mission: Waypoint[];
}

export function listCarrierPlans(): Promise<ApiResult<{ plans: CarrierPlan[] }>> {
  return api.get('/carriers', { logged: false });
}

export function setCarrierPlan(
  carrierId: string,
  embarked: EmbarkRequest[],
): Promise<ApiResult<{ plan: CarrierPlan }>> {
  return api.put(`/carriers/${encodeURIComponent(carrierId)}`, { embarked }, { label: `CARRIER PLAN ${carrierId}` });
}

export function clearCarrierPlan(carrierId: string): Promise<ApiResult<{ removed: string }>> {
  return api.delete(`/carriers/${encodeURIComponent(carrierId)}`, { label: `CARRIER CLEAR ${carrierId}` });
}

export function launchFromCarrier(
  carrierId: string,
  droneIds: string[] = [],
): Promise<ApiResult<{ launching: string[] }>> {
  return api.post(`/carriers/${encodeURIComponent(carrierId)}/launch`, { droneIds }, { label: `CARRIER LAUNCH ${carrierId}` });
}

export function recoverToCarrier(carrierId: string, droneId: string): Promise<ApiResult<{ plan: CarrierPlan }>> {
  return api.post(`/carriers/${encodeURIComponent(carrierId)}/recover`, { droneId }, { label: `CARRIER RECOVER ${droneId}` });
}
